import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "python",
  name: "Python",
  nameEn: "Python",
  seo: {
    title: "Pythonを学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "PythonはAI・機械学習・データ分析・Web開発まで幅広く活躍する言語です。ゆめさくエンジニアなら未経験からでも実践的なPythonスキルを習得できます。",
    url: "/columns/python-programming-school",
  },
  hero: {
    title: "Pythonを学べる女性向けプログラミングスクール",
    englishTitle: "Learn Python",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "Python" },
  ],
  overview: {
    heading: "Pythonとは？",
    paragraphs: [
      "Pythonは、シンプルで読みやすい文法が特徴のプログラミング言語です。1991年にグイド・ヴァンロッサムによって開発され、現在では世界で最も人気のある言語の一つとなっています。",
      "AI・機械学習、データ分析、Web開発（Django/Flask）、業務自動化など、幅広い分野で活用されており、初心者が最初に学ぶ言語としても圧倒的な人気を誇ります。",
      "Google、Netflix、Instagram、NASAなど世界的な企業・組織でも採用されており、豊富なライブラリとコミュニティが学習を強力にサポートします。",
    ],
    useCases: [
      {
        label: "データ分析",
        description:
          "pandas・NumPyを使ったデータの集計・可視化。マーケティングや経営分析の現場で活躍します。",
      },
      {
        label: "AI・機械学習",
        description:
          "TensorFlow・PyTorchなどのフレームワークで、画像認識や自然言語処理のモデルを構築できます。",
      },
      {
        label: "Webアプリ開発",
        description:
          "DjangoやFlaskを使って、サーバーサイドのWebアプリケーションを効率的に開発できます。",
      },
      {
        label: "業務自動化",
        description:
          "Excel操作、メール送信、Webスクレイピングなど、日々の反復作業を自動化して業務効率を大幅に改善します。",
      },
    ],
  },
  whyLearn: {
    heading: "Pythonを学ぶべき3つの理由",
    items: [
      {
        title: "求人数が多く、キャリアの選択肢が広い",
        description:
          "Python関連の求人は年々増加しており、データサイエンティスト・AIエンジニア・バックエンド開発者など多様なキャリアパスが開けます。リモートワーク求人の割合も高く、柔軟な働き方を実現しやすい言語です。",
      },
      {
        title: "初心者でも読みやすく、挫折しにくい",
        description:
          "Pythonは英語に近い自然な文法で書けるため、プログラミング未経験者でも直感的に理解できます。インデントで構造を表すシンプルな設計により、コードの可読性が高く、学習の初期段階でつまずきにくいのが特徴です。",
      },
      {
        title: "AI・データサイエンス分野の標準言語",
        description:
          "ChatGPTをはじめとするAIブームにより、Pythonの需要はさらに加速しています。機械学習やデータサイエンスの研究・実務で事実上の標準となっており、この分野でのキャリアを目指すなら必須の言語です。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでPythonを学ぶ",
    paragraphs: [
      "ゆめさくエンジニアのカリキュラムでは、Web開発の基礎を学んだ後、Pythonを使ったAI・機械学習モジュールに進みます。実際のデータセットを使った分析プロジェクトや、機械学習モデルの構築を通じて実践力を養います。",
      "メンターによる個別サポートがあるため、数学やアルゴリズムに不安がある方でも安心して学習を進められます。",
    ],
    topics: [
      "Python基礎文法・データ型・制御構文",
      "pandas・NumPyによるデータ処理",
      "matplotlibによるデータ可視化",
      "scikit-learnによる機械学習入門",
      "Webスクレイピング・API連携",
      "実践プロジェクト：データ分析レポート作成",
    ],
  },
  careerPaths: {
    heading: "Pythonを活かせるキャリア",
    items: [
      {
        title: "データサイエンティスト",
        description:
          "企業のデータを分析し、ビジネス上の意思決定をサポートする職種です。統計学の知識とPythonのデータ分析スキルを組み合わせて、売上予測やユーザー行動分析を行います。",
        salary: "500〜800万円",
      },
      {
        title: "AIエンジニア",
        description:
          "機械学習モデルの設計・開発・運用を担当します。画像認識、自然言語処理、レコメンドシステムなど、AI技術を使ったプロダクト開発に携わります。",
        salary: "600〜1,000万円",
      },
      {
        title: "バックエンドエンジニア",
        description:
          "DjangoやFlaskを使ったWebアプリケーションのサーバーサイド開発を担当します。API設計やデータベース設計など、システムの裏側を支える重要な役割です。",
        salary: "450〜700万円",
      },
      {
        title: "業務自動化エンジニア",
        description:
          "Pythonを使って企業の業務プロセスを自動化するスクリプトやツールを開発します。RPAとの組み合わせでDX推進の中核を担います。",
        salary: "400〜600万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "6〜12ヶ月（独学で基礎習得）",
        luagate: "3〜4ヶ月（効率的なカリキュラム）",
      },
      {
        label: "挫折率",
        selfStudy: "約90%が途中で挫折",
        luagate: "メンターサポートで挫折率を大幅に低減",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "自分でテーマを探す必要あり",
        luagate: "実務レベルのプロジェクトを複数経験",
      },
      {
        label: "質問対応",
        selfStudy: "検索やQ&Aサイトに頼る",
        luagate: "現役エンジニアのメンターに直接質問可能",
      },
      {
        label: "就職サポート",
        selfStudy: "すべて自力で進める",
        luagate: "ポートフォリオ添削・面接対策まで一貫サポート",
      },
    ],
  },
  faq: [
    {
      question: "Pythonを学ぶのに数学の知識は必要ですか？",
      answer:
        "Web開発や業務自動化であれば、中学数学レベルで十分です。AI・機械学習に進む場合は線形代数や統計の基礎があると有利ですが、ゆめさくエンジニアのカリキュラムでは必要な数学知識も合わせて学べるため、事前準備は不要です。",
    },
    {
      question: "プログラミング未経験でもPythonから始められますか？",
      answer:
        "はい、Pythonは未経験者が最初に学ぶ言語として最も推奨されています。シンプルな文法と豊富な学習リソースがあり、ゆめさくエンジニアでもゼロから段階的に学べるカリキュラムを用意しています。",
    },
    {
      question: "PythonとJavaScriptの違いは何ですか？",
      answer:
        "Pythonはデータ分析・AI・バックエンド開発に強く、JavaScriptはWebのフロントエンド開発に必須の言語です。目的に応じて選ぶのがおすすめですが、どちらも学ぶことで活躍の幅が大きく広がります。",
    },
    {
      question: "Pythonの学習にどれくらいの時間がかかりますか？",
      answer:
        "基礎文法の習得には1〜2ヶ月、実務レベルのスキルを身につけるには3〜6ヶ月が目安です。ゆめさくエンジニアでは効率的なカリキュラムにより、3〜4ヶ月で実践的なスキルを習得できます。",
    },
    {
      question: "Pythonを学んだ後、どんな仕事ができますか？",
      answer:
        "データサイエンティスト、AIエンジニア、バックエンドエンジニア、業務自動化エンジニアなど、幅広い職種で活躍できます。特にAI・データ分析分野は需要が急増しており、高い年収が期待できます。",
    },
  ],
  relatedLanguages: [
    { slug: "javascript", name: "JavaScript" },
    { slug: "sql", name: "SQL" },
    { slug: "html-css", name: "HTML/CSS" },
    { slug: "react", name: "React" },
  ],
};
